import React, { useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { API } from '../../App';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { TrendingUp } from 'lucide-react';
import { fmtRM } from './payslipEditUtils';

const today = () => new Date().toISOString().slice(0, 10);

export function SalaryRevisionDialog({ open, onOpenChange, employees, onCreated }) {
  const [employeeId, setEmployeeId] = useState('');
  const [mode, setMode] = useState('amount');
  const [newBasic, setNewBasic] = useState('');
  const [pct, setPct] = useState('');
  const [effectiveDate, setEffectiveDate] = useState(today());
  const [remarks, setRemarks] = useState('');
  const [review, setReview] = useState(null);
  const [saving, setSaving] = useState(false);

  const emp = useMemo(() => employees.find((e) => e.employee_id === employeeId), [employees, employeeId]);
  const oldBasic = Number(emp?.salary_basic ?? emp?.basic_salary ?? 0);

  useEffect(() => {
    if (!open) return;
    setEmployeeId(''); setMode('amount'); setNewBasic(''); setPct(''); setEffectiveDate(today()); setRemarks(''); setReview(null);
  }, [open]);

  useEffect(() => {
    if (!employeeId) { setReview(null); return; }
    axios.get(`${API}/performance?employee_id=${employeeId}`)
      .then((r) => {
        const rows = Array.isArray(r.data) ? r.data : [];
        rows.sort((a, b) => String(b.created_at || '').localeCompare(String(a.created_at || '')));
        setReview(rows[0] || null);
      })
      .catch(() => setReview(null));
  }, [employeeId]);

  const computedNew = mode === 'amount' ? Number(newBasic || 0) : Math.round(oldBasic * (1 + Number(pct || 0) / 100) * 100) / 100;
  const increment = computedNew - oldBasic;
  const incPct = oldBasic > 0 ? (increment / oldBasic) * 100 : 0;

  const submit = async () => {
    if (!employeeId) { toast.error('Select an employee'); return; }
    if (!(computedNew > 0)) { toast.error('Enter a valid new basic salary'); return; }
    setSaving(true);
    try {
      const body = { employee_id: employeeId, effective_date: effectiveDate, remarks: remarks.trim(), review_id: review?.review_id || null };
      if (mode === 'amount') body.new_basic = Number(newBasic); else body.increment_pct = Number(pct);
      const res = await axios.post(`${API}/payroll/salary-revisions`, body);
      const applied = res.data?.revision?.status === 'applied';
      toast.success(applied ? 'Salary revision applied — employee profile updated' : `Salary revision scheduled for ${effectiveDate}`);
      onOpenChange(false);
      onCreated?.(res.data?.revision);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to create salary revision');
    } finally {
      setSaving(false);
    }
  };

  const activeEmployees = employees.filter((e) => (e.status || 'active') === 'active');

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg dark:bg-slate-800" data-testid="salary-revision-dialog">
        <DialogHeader>
          <DialogTitle className="dark:text-slate-100 flex items-center gap-2"><TrendingUp className="h-5 w-5 text-emerald-500" /> New Salary Revision</DialogTitle>
        </DialogHeader>
        <div className="space-y-4">
          <div>
            <Label className="text-sm">Employee</Label>
            <select value={employeeId} onChange={(e) => setEmployeeId(e.target.value)} data-testid="revision-employee-select"
              className="mt-1 w-full h-9 rounded-lg border border-slate-300 dark:border-slate-600 px-3 text-sm dark:bg-slate-900 dark:text-slate-100">
              <option value="">Select employee…</option>
              {activeEmployees.map((e) => <option key={e.employee_id} value={e.employee_id}>{e.full_name} — {e.employee_id}</option>)}
            </select>
          </div>

          {emp && (
            <div className="grid grid-cols-2 gap-3 rounded-lg bg-slate-50 dark:bg-slate-900 p-3 text-sm" data-testid="revision-employee-summary">
              <div><p className="text-xs text-slate-500">Current basic</p><p className="font-semibold tabular-nums" data-testid="revision-current-basic">{fmtRM(oldBasic)}</p></div>
              <div>
                <p className="text-xs text-slate-500">Latest performance rating</p>
                <p className="font-semibold" data-testid="revision-rating">{review ? `${review.rating}/10 · ${review.period || review.review_period || ''}` : 'No review on file'}</p>
              </div>
            </div>
          )}

          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label className="text-sm">Revision type</Label>
              <select value={mode} onChange={(e) => setMode(e.target.value)} data-testid="revision-mode-select"
                className="mt-1 w-full h-9 rounded-lg border border-slate-300 dark:border-slate-600 px-3 text-sm dark:bg-slate-900 dark:text-slate-100">
                <option value="amount">New basic salary (RM)</option>
                <option value="pct">Increment %</option>
              </select>
            </div>
            <div>
              <Label className="text-sm">{mode === 'amount' ? 'New basic (RM)' : 'Increment (%)'}</Label>
              {mode === 'amount'
                ? <Input type="number" step="0.01" min="0" value={newBasic} onChange={(e) => setNewBasic(e.target.value)} data-testid="revision-new-basic-input" className="mt-1 h-9" />
                : <Input type="number" step="0.1" value={pct} onChange={(e) => setPct(e.target.value)} data-testid="revision-pct-input" className="mt-1 h-9" />}
            </div>
          </div>

          <div>
            <Label className="text-sm">Effective date</Label>
            <Input type="date" value={effectiveDate} onChange={(e) => setEffectiveDate(e.target.value)} data-testid="revision-effective-date" className="mt-1 h-9" />
            <p className="text-xs text-slate-500 mt-1">If the date is today or earlier, the profile updates immediately. Future dates apply automatically on that day.</p>
          </div>

          {emp && computedNew > 0 && (
            <div className="rounded-lg border border-emerald-200 dark:border-emerald-800 bg-emerald-50 dark:bg-emerald-900/20 p-3 text-sm flex items-center justify-between" data-testid="revision-preview">
              <span className="text-emerald-900 dark:text-emerald-200">{fmtRM(oldBasic)} → <strong>{fmtRM(computedNew)}</strong></span>
              <span className={`font-semibold ${increment >= 0 ? 'text-emerald-700' : 'text-rose-700'}`}>{increment >= 0 ? '+' : '−'}{fmtRM(Math.abs(increment))} ({incPct.toFixed(2)}%)</span>
            </div>
          )}

          <div>
            <Label className="text-sm">Remarks</Label>
            <Textarea rows={2} value={remarks} onChange={(e) => setRemarks(e.target.value)} data-testid="revision-remarks-input" className="mt-1 text-sm" placeholder="e.g. Annual appraisal 2026 — exceeded targets" />
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)} disabled={saving} data-testid="revision-cancel-btn">Cancel</Button>
          <Button onClick={submit} disabled={saving} data-testid="revision-submit-btn" className="bg-emerald-600 hover:bg-emerald-700 text-white">{saving ? 'Saving…' : 'Save Revision'}</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
